import React, { useId, useState } from 'react'
import { cn, type UtilityClassNames } from '@s4l/utility-css'
import Tooltip from '../../Atoms/Tooltip'
import Typography from '../../Atoms/Typography'
import styles from './index.module.css'

export interface SegmentedControlProps {
  items: Array<{ value: string; label: React.ReactNode; title?: string }>
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
  size?: 's' | 'm'
  className?: UtilityClassNames
  dataTestId?: string
}

const SegmentedControl = ({
  items,
  value: controlledValue,
  defaultValue,
  onValueChange,
  disabled = false,
  size = 'm',
  className,
  dataTestId,
}: SegmentedControlProps) => {
  const [internalValue, setInternalValue] = useState(defaultValue ?? items[0]?.value)
  const groupName = useId()

  const isControlled = controlledValue !== undefined
  const selectedValue = isControlled ? controlledValue : internalValue

  const selectedIndex = items.findIndex(item => item.value === selectedValue)

  const handleSelect = (value: string) => {
    if (disabled) return

    if (!isControlled) {
      setInternalValue(value)
    }

    onValueChange?.(value)
  }

  return (
    <div
      role="radiogroup"
      className={cn(styles.root, className)}
      data-size={size}
      data-disabled={disabled || undefined}
      data-testid={dataTestId}
      style={
        {
          '--segment-count': items.length,
          '--selected-index': selectedIndex >= 0 ? selectedIndex : 0,
        } as React.CSSProperties
      }
    >
      <div className={styles.indicator} />
      {items.map(item => {
        const isSelected = item.value === selectedValue

        return (
          <Tooltip key={item.value} enabled={!!item.title} delayClose={0}>
            <Tooltip.Trigger>
              <Typography
                as="label"
                variant="body"
                size="m"
                bold
                className={styles.item as UtilityClassNames}
                data-selected={isSelected || undefined}
              >
                <input
                  type="radio"
                  name={groupName}
                  value={item.value}
                  checked={isSelected}
                  disabled={disabled}
                  className={styles.radio}
                  aria-label={item.title}
                  onChange={() => handleSelect(item.value)}
                />
                {item.label}
              </Typography>
            </Tooltip.Trigger>
            <Tooltip.Content>{item.title}</Tooltip.Content>
          </Tooltip>
        )
      })}
    </div>
  )
}

SegmentedControl.displayName = 'SegmentedControl'
export default SegmentedControl
